import { useEffect, useState } from 'react';

import { useAtomValue, useStore } from 'jotai';

import { LoanOfferT, useCurrentStep } from '@consumer-frontend/api';

import { isSupportedContractLoanType } from '../config';
import { loanOfferAtom, loanOfferLoadedAtAtom } from './atoms';
import { clearFlowStorage, processOrchestratorResponse, redirectIfUnauthorized } from './processOrchestratorResponse';

const NOT_ON_CONTRACT_STATUSES = [404, 410];

export type UseLoadLoanOfferT = {
  hasNoContractOffer: boolean;
  isError: boolean;
  isLoading: boolean;
  errorStatus?: number;
  loanOffer?: LoanOfferT;
};

/**
 * Fetches the orchestrator flow's current step and lets processOrchestratorResponse route its
 * data into the store — but only until loanOfferAtom has data. current_step is fetched
 * immutable (never revalidates), so once a later submit (display_loan_offer/contract) has moved
 * the offer forward, remounting a page that calls this hook (e.g. navigating back to a previous
 * step) must not resync that stale pre-submission snapshot back over it.
 */
export const useLoadLoanOffer = (flowId: string): UseLoadLoanOfferT => {
  const store = useStore();
  const loanOffer = useAtomValue(loanOfferAtom) ?? undefined;
  const hasLoanOffer = loanOffer !== undefined;
  const { error, isLoading, response } = useCurrentStep(hasLoanOffer ? '' : flowId);
  // A response can "succeed" (200, no network error) with nothing this hook's callers can do
  // anything with — e.g. a terminated flow whose last recorded step carries no route, or an
  // unrecognized route — in which case processOrchestratorResponse has nowhere to redirect and
  // no offer to store. Left unreported, that's an empty contractStepAtom forever, which is a
  // permanent loading spinner on ContractIndexRedirect rather than a page that ever resolves.
  const [isDeadEnd, setIsDeadEnd] = useState(false);

  useEffect(
    () => {
      if (error) {
        if (redirectIfUnauthorized(error, store.set)) return;

        // A non-401 failure here shows the terminal Error page below (no retry) — the flow is
        // effectively over, so its sessionStorage-backed progress (flowId, furthest step, Funds
        // inputs) shouldn't keep sitting there past that point.
        clearFlowStorage(store.set);

        return;
      }

      if (!response) return;

      const outcome = processOrchestratorResponse(response, store.set);

      // Stamp the first load only — the guard keeps it stable across the remounts this hook is
      // built to tolerate (e.g. navigating back a step), so it stays the true initial baseline.
      if (store.get(loanOfferLoadedAtAtom) === null && store.get(loanOfferAtom) !== null) {
        store.set(loanOfferLoadedAtAtom, Date.now());
      }

      // A response that resolved without redirecting but left no offer in the store is a dead end:
      // processOrchestratorResponse's `errors` branch returns 'failed' without writing loan_offer
      // (even when the payload carries one alongside the errors), and a recognized route that
      // simply carried no loan_offer leaves nothing to render either. The store — not the raw
      // response — is the source of truth. A 'redirecting' outcome is already navigating away, so
      // it is deliberately excluded rather than flagged as a dead end.
      setIsDeadEnd(outcome !== 'redirecting' && store.get(loanOfferAtom) === null);
    },
    [error, response, store],
  );

  const errorStatus = error?.response?.status ?? error?.status;
  const isError = !!error || isDeadEnd;
  const respondedNoContract = isError
    && errorStatus !== undefined
    && NOT_ON_CONTRACT_STATUSES.includes(errorStatus);
  const loanTypeIsUnsupported = !!loanOffer && !isSupportedContractLoanType(loanOffer.loan_type);
  const hasNoContractOffer = !isLoading
    && (respondedNoContract || loanTypeIsUnsupported);

  return {
    errorStatus,
    hasNoContractOffer,
    isError,
    isLoading,
    loanOffer,
  };
};
